import React, { useCallback, useState } from "react";
import {
  DndContext,
  DragEndEvent,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  GripVertical,
  X,
  Plus,
  FileText,
  Image,
  Video,
  Mic,
  Link,
  File,
} from "lucide-react";
import EntityPickerModal from "@/components/EntityPickerModal/EntityPickerModal";
import { getResources } from "@/api/getResources/getResourcesApi";

export interface ResourceEntity {
  id: number;
  name: string;
  thumbnailUrl?: string | null;
  primaryIcon?: string | null;
  resourceType?: string | null;
  createdAt?: string;
}

interface ResourceReferenceEditorProps {
  value: ResourceEntity[];
  onChange: (references: ResourceEntity[]) => void;
}

interface SortableResourceCardProps {
  resource: ResourceEntity;
  onRemove: () => void;
}

function SortableResourceCard({
  resource,
  onRemove,
}: SortableResourceCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: resource.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getResourceIcon = () => {
    const type = resource.resourceType?.toLowerCase();
    if (type === "video") return <Video className="h-3 w-3" />;
    if (type === "podcast") return <Mic className="h-3 w-3" />;
    if (type === "article") return <FileText className="h-3 w-3" />;
    if (type === "file") return <File className="h-3 w-3" />;
    if (type === "partner page") return <Link className="h-3 w-3" />;
    return <FileText className="h-3 w-3" />;
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card className="p-2">
        <div className="flex items-center gap-2">
          {/* Drag Handle */}
          <button
            className="cursor-grab hover:cursor-grabbing text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            type="button"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4" />
          </button>

          {/* Thumbnail or Icon */}
          <div className="flex-shrink-0 w-8 h-8 rounded overflow-hidden bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
            {resource.thumbnailUrl ? (
              <img
                alt={resource.name}
                className="w-full h-full object-cover"
                src={resource.thumbnailUrl}
              />
            ) : resource.primaryIcon ? (
              <span
                className="text-gray-500 scale-50"
                dangerouslySetInnerHTML={{ __html: resource.primaryIcon }}
              />
            ) : (
              <div className="text-gray-400">{getResourceIcon()}</div>
            )}
          </div>

          {/* Resource Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              {resource.resourceType && (
                <Badge className="text-xs py-0 px-1" variant="secondary">
                  {resource.resourceType}
                </Badge>
              )}
              <span className="text-sm font-medium truncate">
                {resource.name}
              </span>
            </div>
          </div>

          {/* Remove Button */}
          <button
            className="text-gray-400 hover:text-red-600 transition-colors"
            onClick={onRemove}
            type="button"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </Card>
    </div>
  );
}

export function ResourceReferenceEditor({
  value,
  onChange,
}: ResourceReferenceEditorProps) {
  const [modalOpen, setModalOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = value.findIndex((resource) => resource.id === active.id);
      const newIndex = value.findIndex((resource) => resource.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newResources = arrayMove(value, oldIndex, newIndex);
        onChange(newResources);
      }
    },
    [value, onChange],
  );

  const handleRemove = useCallback(
    (resource: ResourceEntity) => {
      onChange(value.filter((r) => r.id !== resource.id));
    },
    [value, onChange],
  );

  const handleConfirm = useCallback(
    (newSelection: ResourceEntity[]) => {
      onChange(newSelection);
      setModalOpen(false);
    },
    [onChange],
  );

  return (
    <div className="space-y-3">
      {/* Sortable List */}
      {value.length > 0 && (
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          sensors={sensors}
        >
          <SortableContext
            items={value.map((resource) => resource.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2">
              {value.map((resource) => (
                <SortableResourceCard
                  key={`resource-${resource.id}`}
                  onRemove={() => handleRemove(resource)}
                  resource={resource}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {value.length === 0 && (
        <div className="text-center py-6 text-sm text-gray-500 dark:text-gray-400 border border-dashed border-gray-200 dark:border-slate-700 rounded-lg">
          No resources selected. Click below to add resources.
        </div>
      )}

      {/* Add Button */}
      <Button
        onClick={() => setModalOpen(true)}
        size="sm"
        type="button"
        variant="outline"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Resources
      </Button>

      {/* Entity Picker Modal */}
      <EntityPickerModal<ResourceEntity>
        entityNamePlural="Resources"
        entityNameSingular="Resource"
        fetchEntities={async ({ searchTerm, page, pageSize }) => {
          const response = await getResources({
            searchTerm,
            page,
            pageSize,
            sortBy: "title",
            sortOrder: "ASC",
          });
          const totalCount = response.meta?.totalCount ?? response.data.length;

          return {
            data: response.data.map((r) => ({
              id: r.id,
              name: r.title,
              thumbnailUrl: r.thumbnailUrl,
              primaryIcon: r.primaryIcon,
              resourceType: r.resourceType,
              createdAt: r.createdAt,
            })),
            totalCount,
          };
        }}
        initialSelectedEntities={value}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleConfirm}
        renderEntityRow={(resource, isSelected, toggle) => {
          const getIcon = () => {
            const type = resource.resourceType?.toLowerCase();
            if (type === "video") return <Video className="h-4 w-4" />;
            if (type === "podcast") return <Mic className="h-4 w-4" />;
            if (type === "article") return <FileText className="h-4 w-4" />;
            if (type === "file") return <File className="h-4 w-4" />;
            if (type === "partner page") return <Link className="h-4 w-4" />;
            return <FileText className="h-4 w-4" />;
          };

          return (
            <div
              className={`
                flex items-center gap-2.5 px-2.5 py-2 border border-gray-200 dark:border-slate-700 rounded-md cursor-pointer
                transition-colors hover:bg-gray-50 dark:hover:bg-slate-800
                ${isSelected ? "bg-blue-50 dark:bg-blue-900/20 border-blue-500 dark:border-blue-500" : ""}
              `}
              onClick={() => toggle(resource)}
            >
              {/* Checkbox */}
              <input
                checked={isSelected}
                className="rounded h-3.5 w-3.5"
                onChange={() => toggle(resource)}
                type="checkbox"
              />

              {/* Thumbnail or Icon */}
              <div className="flex-shrink-0 w-8 h-8 rounded overflow-hidden bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                {resource.thumbnailUrl ? (
                  <img
                    alt={resource.name}
                    className="w-full h-full object-cover"
                    src={resource.thumbnailUrl}
                  />
                ) : resource.primaryIcon ? (
                  <span
                    className="text-gray-500 scale-50"
                    dangerouslySetInnerHTML={{ __html: resource.primaryIcon }}
                  />
                ) : (
                  <div className="text-gray-400">{getIcon()}</div>
                )}
              </div>

              {/* Resource Info */}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{resource.name}</div>
                {resource.resourceType && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {resource.resourceType}
                  </span>
                )}
              </div>
            </div>
          );
        }}
      />
    </div>
  );
}
